import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import Avatar from '@jimmyboys/liita-ui/dist/components/Avatar'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'
import ModalOptions from '@jimmyboys/liita-ui/dist/components/ModalOptions'
import Skeleton from '@jimmyboys/liita-ui/dist/components/Skeleton'
import useQueryParams from '@jimmyboys/liita-ui/dist/hooks/useQueryParams'
import FuseHighlight from 'components/FuseHighlight/FuseHighlight'
import React, { memo, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { matchPath } from 'react-router-dom/cjs/react-router-dom.min'
import { useLocation } from 'react-use'
import TaskStatus from '../UserTaskRow/TaskStatus'

import styles from './NotificationCard.module.sass'
import { useDrawerContext } from 'context/Drawer/DrawerContext'
import useSession from 'hooks/useSession'

const MANAGEMENT_OPINION = 'Parecer De Gestão'

const NotificationCard = memo(
	(props) => {
		const { className, data, ...attributes } = props
		const { user } = useSession()
		const {
			closeNotification,
			postponeNotification,
			markNotificationAsRead,
			markNotificationAsUnread,
			replyNotification,
			forwardNotification,
			i18n,
		} = useGlobal()
		const applyCardBackground = data.$applyCardBackground
		const Tag = data.link ? Link : 'div'
		const to = data.link?.to
		const location = useLocation()
		const [queryParams] = useQueryParams(location)
		const [processId, setProcessId] = useState(null)
		const { openDrawer } = useDrawerContext()
		// Removed AT and backoffice role, because backoffice is to be removed
		// const isAT = data.causaltyBranch === 'AT'
		// const hasBackofficeRole = user?.roles?.some((role) =>
		// 	role?.startsWith('backoffice')
		// )

		useEffect(() => {
			const match = matchPath(location?.pathname, {
				path: '/clinical-processes/:clinicalProcessId',
				strict: false,
				exact: false,
			})

			if (match?.params?.clinicalProcessId) {
				setProcessId((prevProcessId) =>
					prevProcessId !== match?.params?.clinicalProcessId
						? match?.params?.clinicalProcessId
						: prevProcessId
				)
			} else {
				setProcessId(null)
			}
		}, [location?.pathname])

		const notificationOptions = [
			{
				value: 'reply',
				text: data.$cardLabels?.reply,
				action: () => {
					replyNotification &&
						replyNotification(data.processId, data.thread?.id)
				},
			},
			data.isRead
				? {
						value: 'cancelled',
						text: data.$cardLabels?.unread,
						action: () => {
							markNotificationAsUnread &&
								markNotificationAsUnread(data.id, processId)
						},
				  }
				: {
						value: 'success',
						text: data.$cardLabels?.read,
						action: () => {
							markNotificationAsRead &&
								markNotificationAsRead(data.id, processId)
						},
				  },
			{
				value: 'rejected',
				text: data.$cardLabels?.reject,
				action: () => {
					closeNotification && closeNotification(data.id, processId)
				},
			},
			{
				value: 'postpone',
				text: data.$cardLabels?.postpone,
				action: () => {
					postponeNotification &&
						postponeNotification(data.id, processId)
				},
			},
		]

		// Removed forward to backoffice, because backoffice is to be removed
		// if (!isAT && hasBackofficeRole) {
		// 	notificationOptions.push({
		// 		value: 'forward',
		// 		text: data.$cardLabels?.forward,
		// 		action: () => {
		// 			forwardNotification &&
		// 				forwardNotification(data.id, processId)
		// 		},
		// 	})
		// }

		return (
			<div
				{...attributes}
				className={`card ${styles['root'] || ''} ${className || ''} ${
					applyCardBackground ? styles['--with-background'] : ''
				}
				${data?.closed ? styles['--disabled'] : ''}
				${
					parseInt(queryParams.notificationId, 10) === data.id
						? styles['--selected']
						: ''
				}`}
			>
				<span className={styles['avatar-container']}>
					<Avatar
						rounded
						className={styles['avatar']}
						user={data.creationUser}
					/>
				</span>
				<span className={styles['status-container']}>
					{data.isForwarded && (
						<Icon name='forward' height='15px' width='15px' />
					)}
					{!data.isRead && (
						<TaskStatus
							status='on-time'
							className={styles['status-icon']}
						/>
					)}
				</span>
				<div className={styles['details-container']}>
					<Tag
						to={to}
						onClick={() => {
							if (
								data.opinion?.opinionType === MANAGEMENT_OPINION
							) {
								openDrawer(
									'ManagementOpinionDrawer',
									data?.opinion,
									`/clinical-processes/${data.processId}?notificationId=${data.id}`
								)
							}
						}}
					>
						<div className={styles['details-header']}>
							<h5>{data.title}</h5>
							<small className={styles['author']}>
								<FuseHighlight
									matchedWords={
										data.$textHighlight?.matchedIndexes?.[
											'creationUser.name'
										]
									}
									text={
										data.creationUser?.name ||
										i18n`Not defined`
									}
								/>
								{`, ${
									data.sourceEstablishment ||
									i18n`Not defined`
								}`}
							</small>
						</div>
						<div
							dynamic='true'
							className={styles['notification-body']}
						>
							<div
								style={{
									minHeight:
										!isNaN(data.textHeight) &&
										data.textHeight > 0
											? data.textHeight
											: undefined,
									maxHeight:
										!isNaN(data.textHeight) &&
										data.textHeight > 0
											? data.textHeight
											: undefined,
								}}
							>
								{data.text && (
									<FuseHighlight
										matchedWords={
											data.$textHighlight
												?.matchedIndexes?.['text']
										}
										text={data.text}
									/>
								)}{' '}
							</div>
						</div>
						<div className={styles['footer']}>
							<div>
								<small className={styles['notification-id']}>
									<strong>#{data.processId}</strong>
								</small>
								<small>
									<Icon
										className={styles['text-icon']}
										width='12px'
										height='12px'
										name='calendar'
									/>
									{data.date?.dateTimeFormat}
								</small>
							</div>
						</div>
					</Tag>
					<div className={styles['modal-options']}>
						<ModalOptions
							title={data.$cardLabels?.options}
							options={notificationOptions}
							onSelect={(item) => {
								item && item.action && item.action()
							}}
						>
							<ActionButton
								className={styles['action-button']}
								icon='options'
							/>
						</ModalOptions>
					</div>
				</div>
			</div>
		)
	},
	(prevProps, nextProps) =>
		prevProps.data.id === nextProps.data.id &&
		prevProps.data.textHeight === nextProps.data.textHeight &&
		prevProps.data.isRead === nextProps.data.isRead &&
		prevProps.style?.top === nextProps.style?.top &&
		prevProps['data-focused'] === nextProps['data-focused'] &&
		prevProps.data?.$textHighlight?.query ===
			nextProps.data?.$textHighlight?.query
)
NotificationCard.displayName = 'NotificationCard'
NotificationCard.defaultHeight = 110
NotificationCard.defaultLines = 1

export default NotificationCard

export const NotificationCardSkeleton = (props) => {
	const { className, data, ...attributes } = props
	const applyCardBackground = data.$applyCardBackground

	return (
		<div
			{...attributes}
			className={`${styles['root-skeleton']} ${className || ''} ${
				applyCardBackground ? styles['--with-background'] : ''
			}`}
		>
			<span className={styles['status-container']} />
			<span className={styles['avatar-container']}>
				<Skeleton
					className={`${styles['skeleton']} ${styles['round']}`}
				/>
			</span>
			<div className={styles['details-container']}>
				<div className={styles['details-header']}>
					<Skeleton
						className={`${styles['skeleton']} ${styles['smaller']}`}
					/>
				</div>
				<div className={styles['notification-body']}>
					<Skeleton className={styles['skeleton']} />
					<Skeleton className={styles['skeleton']} />
				</div>
			</div>
		</div>
	)
}

NotificationCardSkeleton.defaultHeight = 81
